import React, { PureComponent } from 'react';
// utils
import isEmpty from 'lodash.isempty';
import get from 'lodash.get';
import { toastResponseErrors } from '../../../utils/response';
import { toast } from 'react-toastify';
import {
  getValuesFromCompleted,
  getDefaultValues,
  prepareValuesToSubmit,
  getValidationSchema,
  postValidation
} from '../utils';
import ReactHtmlParser from 'react-html-parser';
// hoc
import { withFormik } from 'formik';
// components
import StepTitle from './StepTitle';
import Form from '../../../components/Form/Form';
import NavButton from './NavButton';
import Input from '../../../components/Input/Input';
import CheckBoxInput from './CheckBoxInput';
import RadioTable from './RadioTable';
import RadioGroupInput from './RadioGroupInput';
import TextInputsSet from './TextInputsSet';
import InputTable from './InputTable';
import DateInput from './DateInput';
import GoalsInput from './GoalsInput';
import SatisfactionBlock from './SatisfactionTable';
import DynamicInputList from './DynamicInputList';
import ConnectedInput from './ConnectedInput';
// styles
import styles from './InputStep.mod.css';
import classnames from 'classnames/bind';

const cn = classnames.bind(styles);

class InputStep extends PureComponent {
  componentDidMount() {
    window.scrollTo(0, 0);
  }

  componentWillReceiveProps(nextProps) {
    if (
      this.props.isSubmitting &&
      !nextProps.isSubmitting &&
      !isEmpty(nextProps.errors)
    ) {
      toast.error('Please, ensure that all fields are correct!');
    }
  }

  renderInputs = ({ id, content, post_description }) => {
    const {
      values,
      handleChange,
      setFieldValue,
      setValues,
      setFieldTouched,
      handleBlur,
      errors,
      touched
    } = this.props;

    return content.input_items.map((input, index) => {
      const type = input.content
        ? input.content.question_set_type || input.content.question_type
        : input.question_type;

      const inputProps = {
        name: input.id,
        onChange: handleChange,
        placeholder: 'Please, type here',
        label: input.content ? input.content.title : input.title,
        onBlur: handleBlur,
        invalid: errors[input.content.id] && touched[input.content.id],
        defaultValue: values[input.content.id]
      };

      const getInputs = () => {
        switch (type) {
          case 'text':
          case 'number':
            return input.content ? (
              input.content.question_set_type && (
                <TextInputsSet
                  data={input}
                  values={values}
                  errors={errors}
                  setFieldValue={setFieldValue}
                />
              )
            ) : (
              <Input wrapperClassName={cn('input-wrapper')} {...inputProps} />
            );

          case 'hidden_answers':
          case 'checkbox':
            return (
              <CheckBoxInput
                data={input}
                values={values}
                errors={errors}
                setFieldValue={setFieldValue}
              />
            );

          case 'radio':
            return (
              <RadioGroupInput
                data={input}
                values={values}
                errors={errors}
                setFieldValue={setFieldValue}
              />
            );

          case 'date':
            return (
              <DateInput
                data={input}
                values={values}
                errors={errors}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
              />
            );

          case 'fixed_radio':
            return (
              <RadioTable
                data={input}
                values={values}
                errors={errors}
                setFieldValue={setFieldValue}
              />
            );

          case 'fixed_input':
          case 'fixed_input_preliminary':
          case 'fixed_input_related':
            return (
              <InputTable
                id={id}
                data={input}
                values={values}
                errors={errors}
                setFieldValue={setFieldValue}
              />
            );

          case 'connected_question':
            return (
              <ConnectedInput
                data={input}
                values={values}
                errors={errors}
                setFieldValue={setFieldValue}
              />
            );

          case 'goals':
            return (
              <GoalsInput
                data={input}
                values={values}
                errors={errors}
                setValues={setValues}
                setFieldValue={setFieldValue}
              />
            );

          case 'dynamic_list':
            return (
              <DynamicInputList
                data={input}
                values={values}
                errors={errors}
                setFieldValue={setFieldValue}
              />
            );

          case 'satisfaction_info':
            return <SatisfactionBlock id={id} data={input} />;

          default:
            return null;
        }
      };

      const withPostDescription =
        !!post_description && Object.keys(values).find(key => values[key]);

      return (
        <div className={cn('inputs-container')} key={index}>
          <h4>{`${index + 1}. ${input.content.title}`}</h4>
          {input.content && input.content.question_set_type === 'mixed' && (
            <div className={cn('sub-inputs')}>
              {this.renderInputs(input.content.questions)}
            </div>
          )}
          {getInputs()}
          {withPostDescription && (
            <span className={cn('post-description')}>
              {ReactHtmlParser(post_description)}
            </span>
          )}
        </div>
      );
    });
  };

  render() {
    const {
      isSubmitting,
      handleSubmit,
      activeStep,
      goPrevStep,
      data
    } = this.props;

    return (
      <Form className={cn('container')} onSubmit={handleSubmit}>
        <StepTitle title={data.title} description={data.description} />
        <div className={cn('inputs-wrapper')}>{this.renderInputs(data)}</div>
        <div className={cn('nav-buttons')}>
          <NavButton direction="next" type="submit" disabled={isSubmitting}>
            Next
          </NavButton>
          {!!activeStep && (
            <NavButton
              direction="prev"
              type="button"
              onClick={goPrevStep}
              disabled={isSubmitting}
            >
              Prev
            </NavButton>
          )}
        </div>
      </Form>
    );
  }
}

export default withFormik({
  enableReinitialize: true,
  validateOnChange: false,
  mapPropsToValues: ({ completedData, data }) =>
    completedData
      ? getValuesFromCompleted(completedData, data.content)
      : getDefaultValues(data.content),
  validationSchema: ({ data }) => getValidationSchema(data),
  handleSubmit: (
    values,
    {
      setSubmitting,
      props: {
        data,
        history,
        submitStep,
        updateStep,
        activeStep,
        stepsLength,
        setActiveStep,
        completedData
      }
    }
  ) => {
    const error = postValidation({ data, values });

    const type = get(data, 'content.input_items.0.content.question_set_type');

    const onUpdate = () => {
      setSubmitting(false);
      window.scrollTo(0, 0);
      activeStep === stepsLength - 1
        ? history.push('/dashboard')
        : setActiveStep(activeStep + 1);
    };

    const onError = e => {
      setSubmitting(false);
      toastResponseErrors(e);
    };

    if (error) {
      toastResponseErrors(error);
      setSubmitting(false);
      return;
    }

    setSubmitting(true);
    const input_items_data = prepareValuesToSubmit({ data, values });
    const resource = {
      step_id: data.id,
      input_items_data
    };

    if (completedData) {
      if (type === 'satisfaction_info') {
        return onUpdate();
      }
      updateStep({ resource, id: completedData.id })
        .then(onUpdate)
        .catch(onError);
    } else {
      submitStep(resource)
        .then(() => {
          window.scrollTo(0, 0);
        })
        .catch(onError);
    }
  }
})(InputStep);



// WEBPACK FOOTER //
// ./src/containers/Module/components/InputStep.jsx